/**
 * @file     listingActions.ts
 * @purpose  The one CTA config every listing surface reads: listing state to
 *           badge, primary, secondary and More-menu actions, plus the actions a
 *           state must never render. Hero rail, top bar, phone bar and quick
 *           views all resolve through `listingActionsFor`, so they never
 *           disagree. Contract: docs/specs/LISTING-SURFACES.md.
 */
import { localizedText } from '@/core/content';

export type ListingState =
  | 'active' | 'openhouse' | 'pricechange'
  | 'pending' | 'contract'
  | 'sold' | 'expired' | 'terminated' | 'withdrawn'
  | 'comingsoon' | 'curated' | 'gated' | 'offmarketPublic'
  /** An active MLS® rental: the request is forwarded to the listing brokerage. */
  | 'lease'
  /** A rental Ray lists for its owner (the owner page, or its MLS® copy): the request goes to Ray. */
  | 'leaseOwner'
  /** A rental that is leased, taken down or gone from the feed. */
  | 'leased';

export type ActionId =
  | 'book_showing' | 'attend_open_house' | 'ask_question' | 'get_listing_alerts'
  | 'see_similar' | 'get_area_alerts' | 'value_my_home'
  | 'request_documents' | 'save_pdf' | 'make_offer' | 'broker_intro'
  | 'create_account' | 'sign_in'
  | 'request_viewing' | 'rental_alerts';

export const ACTION_LABEL = {
  book_showing: localizedText('listingActions.label.bookShowing', 'Book a showing'),
  attend_open_house: localizedText('listingActions.label.attendOpenHouse', 'Attend open house'),
  ask_question: localizedText('listingActions.label.askQuestion', 'Ask a question'),
  get_listing_alerts: localizedText('listingActions.label.listingAlerts', 'Get listing alerts'),
  see_similar: localizedText('listingActions.label.seeSimilar', 'See similar homes'),
  get_area_alerts: localizedText('listingActions.label.areaAlerts', 'Get area alerts'),
  value_my_home: localizedText('listingActions.label.valueMyHome', 'Value my home'),
  request_documents: localizedText('listingActions.label.requestDocuments', 'Request documents'),
  save_pdf: localizedText('listingActions.label.savePdf', 'Save PDF'),
  make_offer: localizedText('listingActions.label.makeOffer', 'Make an offer'),
  broker_intro: localizedText('listingActions.label.brokerIntro', 'Request an introduction'),
  create_account: localizedText('listingActions.label.createAccount', 'Create an account'),
  sign_in: localizedText('listingActions.label.signIn', 'Sign in'),
  request_viewing: localizedText('listingActions.label.requestViewing', 'Request a viewing'),
  rental_alerts: localizedText('listingActions.label.rentalAlerts', 'Get alerts for similar rentals'),
} as const satisfies Record<ActionId, { key: string; defaultValue: string }>;

export type BadgeTone = 'success' | 'primary' | 'info' | 'warning' | 'error' | 'muted' | 'accentYellow';

export interface ListingActionConfig {
  primary: ActionId;
  secondary: ActionId[];
  menu: ActionId[];
  /** One line under the buttons, only when the outcome is not obvious. */
  note?: { key: string; defaultValue: string };
  /** Add to list, Compare and Share icon row. */
  icons: boolean;
  /** Actions that must never render in this state (tests assert this). */
  never: ActionId[];
}

const LIVE_MENU: ActionId[] = ['request_documents', 'save_pdf', 'make_offer', 'broker_intro'];
const CLOSED: ActionId[] = ['book_showing', 'attend_open_house', 'make_offer'];
const UNDER_CONTRACT_NOTE = localizedText(
  'listingActions.note.underContract',
  'New {{place}} {{type}} like this one, by email.',
);
const COMING_SOON_NOTE = localizedText(
  'listingActions.note.comingSoon',
  'Showings start when it goes on the market.',
);
const SOLD_LIKE: ListingActionConfig = {
  primary: 'see_similar',
  secondary: ['get_area_alerts', 'value_my_home'],
  menu: ['save_pdf'],
  icons: true,
  never: [...CLOSED, 'ask_question'],
};
const UNDER_CONTRACT: ListingActionConfig = {
  primary: 'get_listing_alerts',
  secondary: ['ask_question'],
  menu: ['save_pdf', 'broker_intro'],
  note: UNDER_CONTRACT_NOTE,
  icons: true,
  never: CLOSED,
};

/** Sale-only actions a rental never offers (RTA: no offers; Relta never represents an MLS® renter). */
const SALE_ONLY: ActionId[] = [
  'book_showing', 'attend_open_house', 'ask_question', 'get_listing_alerts', 'value_my_home',
  'request_documents', 'save_pdf', 'make_offer', 'broker_intro',
];
const RENTAL: Omit<ListingActionConfig, 'note'> = {
  primary: 'request_viewing',
  secondary: ['rental_alerts'],
  menu: [],
  icons: true,
  never: SALE_ONLY,
};

export const LISTING_ACTIONS: Record<ListingState, ListingActionConfig> = {
  active: { primary: 'book_showing', secondary: ['ask_question'], menu: LIVE_MENU, icons: true, never: [] },
  pricechange: { primary: 'book_showing', secondary: ['ask_question'], menu: LIVE_MENU, icons: true, never: [] },
  openhouse: {
    primary: 'attend_open_house',
    secondary: ['book_showing'],
    menu: ['ask_question', ...LIVE_MENU],
    icons: true,
    never: [],
  },
  pending: UNDER_CONTRACT,
  contract: UNDER_CONTRACT,
  sold: SOLD_LIKE,
  expired: SOLD_LIKE,
  terminated: SOLD_LIKE,
  withdrawn: SOLD_LIKE,
  // Board rules: no showings before the listing is on the market.
  comingsoon: {
    primary: 'ask_question',
    secondary: ['get_listing_alerts'],
    menu: ['save_pdf', 'broker_intro'],
    note: COMING_SOON_NOTE,
    icons: true,
    never: CLOSED,
  },
  curated: {
    primary: 'book_showing',
    secondary: ['ask_question'],
    menu: ['request_documents', 'broker_intro'],
    icons: true,
    never: [],
  },
  gated: {
    primary: 'create_account',
    secondary: ['sign_in'],
    menu: [],
    icons: false,
    never: [
      'book_showing', 'attend_open_house', 'ask_question', 'get_listing_alerts', 'see_similar',
      'get_area_alerts', 'value_my_home', 'request_documents', 'save_pdf', 'make_offer', 'broker_intro',
    ],
  },
  offmarketPublic: {
    primary: 'see_similar',
    secondary: ['get_area_alerts'],
    menu: [],
    icons: false,
    never: [...CLOSED, 'ask_question', 'request_documents', 'save_pdf'],
  },
  lease: {
    ...RENTAL,
    note: localizedText('listingActions.note.leaseForward', 'Your request goes to the listing brokerage for this home.'),
  },
  leaseOwner: {
    ...RENTAL,
    note: localizedText('listingActions.note.leaseOwner', 'Your request goes to Ray Rasouli, who represents the owner of this home.'),
  },
  leased: {
    primary: 'see_similar',
    secondary: ['rental_alerts'],
    menu: [],
    icons: false,
    never: [...SALE_ONLY, 'request_viewing'],
  },
};

/** Rental states: the rail shows rent per month and the available date, never a mortgage line. */
export const RENTAL_STATES: ListingState[] = ['lease', 'leaseOwner', 'leased'];

/** Make an offer renders only for signed-in visitors, in states whose menu lists it. */
export const SIGNED_IN_ONLY: ActionId[] = ['make_offer'];

/** States where the home can no longer be bought; the price is the last asking price. */
export const CLOSED_STATES: ListingState[] = ['sold', 'expired', 'terminated', 'withdrawn'];

/** States whose rail labels the price as the asking price and drops the monthly line. */
export const UNDER_CONTRACT_STATES: ListingState[] = ['pending', 'contract'];

export type BadgeId =
  | 'active' | 'new' | 'newlyListed' | 'openHouse' | 'priceChange' | 'pending' | 'contract'
  | 'sold' | 'expired' | 'terminated' | 'withdrawn' | 'comingSoon' | 'offMarket'
  | 'forRent' | 'listedByRay' | 'leased';

export const LISTING_BADGES: Record<BadgeId, { label: { key: string; defaultValue: string }; tone: BadgeTone }> = {
  active: { label: localizedText('listingActions.badge.active', 'Active'), tone: 'success' },
  // Listed 3 days ago or less (freshnessFromListingDate). Never a day count.
  new: { label: localizedText('listingActions.badge.new', 'New'), tone: 'primary' },
  // 14 days or less.
  newlyListed: { label: localizedText('listingActions.badge.newlyListed', 'Newly listed'), tone: 'primary' },
  openHouse: { label: localizedText('listingActions.badge.openHouse', 'Open house {{day}}'), tone: 'info' },
  priceChange: { label: localizedText('listingActions.badge.priceChange', 'Price change'), tone: 'accentYellow' },
  pending: { label: localizedText('listingActions.badge.pending', 'Pending'), tone: 'warning' },
  contract: { label: localizedText('listingActions.badge.contract', 'Active under contract'), tone: 'warning' },
  sold: { label: localizedText('listingActions.badge.sold', 'Sold'), tone: 'error' },
  expired: { label: localizedText('listingActions.badge.expired', 'Expired'), tone: 'muted' },
  terminated: { label: localizedText('listingActions.badge.terminated', 'Terminated'), tone: 'muted' },
  withdrawn: { label: localizedText('listingActions.badge.withdrawn', 'Withdrawn'), tone: 'muted' },
  comingSoon: { label: localizedText('listingActions.badge.comingSoon', 'Coming soon'), tone: 'info' },
  offMarket: { label: localizedText('listingActions.badge.offMarket', 'Off market'), tone: 'muted' },
  forRent: { label: localizedText('listingActions.badge.forRent', 'For rent'), tone: 'success' },
  listedByRay: { label: localizedText('listingActions.badge.listedByRay', 'Listed by Ray Rasouli'), tone: 'primary' },
  leased: { label: localizedText('listingActions.badge.leased', 'Leased'), tone: 'muted' },
};

const STATUS_BADGE: Record<ListingState, BadgeId | null> = {
  active: 'active',
  openhouse: 'active',
  pricechange: 'active',
  curated: 'active',
  pending: 'pending',
  contract: 'contract',
  sold: 'sold',
  expired: 'expired',
  terminated: 'terminated',
  withdrawn: 'withdrawn',
  comingsoon: 'comingSoon',
  offmarketPublic: 'offMarket',
  gated: null,
  lease: 'forRent',
  leaseOwner: 'listedByRay',
  leased: 'leased',
};

export interface ListingBadgeInput {
  state: ListingState;
  freshness?: 'New' | 'Newly Listed' | null;
  /** "Sat, Oct 10" when an open house is upcoming. */
  openHouseDay?: string | null;
  priceChanged?: boolean;
}

/** Badges in display order: status first, then the one most useful extra. */
export function listingBadges({ state, freshness, openHouseDay, priceChanged }: ListingBadgeInput): BadgeId[] {
  const out: BadgeId[] = [];
  const status = STATUS_BADGE[state];
  if (status) out.push(status);
  if (CLOSED_STATES.includes(state) || state === 'offmarketPublic' || state === 'gated' || state === 'leased') return out;
  if (openHouseDay) out.push('openHouse');
  else if (priceChanged) out.push('priceChange');
  else if (freshness === 'New') out.push('new');
  else if (freshness === 'Newly Listed') out.push('newlyListed');
  return out;
}

export interface ListingStateInput {
  source: 'mls' | 'curated' | 'owner';
  /** MLS StandardStatus, the curated status, or the owner listing status. */
  status: string | null | undefined;
  hasUpcomingOpenHouse?: boolean;
  priceChanged?: boolean;
  /** 'lease' for an MLS® rental (Residential Lease). Owner listings are always rentals. */
  listingType?: 'sale' | 'lease';
  /** An MLS® rental that is Ray's own owner listing (matched by mls_listing_key): the request goes to Ray. */
  ownerCopy?: boolean;
}

const normalize = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, '');

/**
 * Feed status to listing state. Anything unrecognised resolves to a closed
 * state: a home whose status cannot be read never offers a showing or offer.
 */
export function resolveListingState({ source, status, hasUpcomingOpenHouse, priceChanged, listingType, ownerCopy }: ListingStateInput): ListingState {
  const s = normalize(status ?? '');
  if (source === 'owner') return s === 'live' ? 'leaseOwner' : 'leased';
  if (source === 'mls' && listingType === 'lease') {
    if (s !== 'active') return 'leased';
    return ownerCopy ? 'leaseOwner' : 'lease';
  }
  if (source === 'curated') {
    if (s === 'pending') return 'pending';
    if (s === 'sold' || s === 'closed') return 'sold';
    if (s === 'presale' || s === 'comingsoon') return 'comingsoon';
    if (s === 'active') return hasUpcomingOpenHouse ? 'openhouse' : 'curated';
    return 'withdrawn';
  }
  switch (s) {
    case 'active':
      if (hasUpcomingOpenHouse) return 'openhouse';
      return priceChanged ? 'pricechange' : 'active';
    case 'activeundercontract':
      return 'contract';
    case 'pending':
      return 'pending';
    case 'comingsoon':
      return 'comingsoon';
    case 'closed':
    case 'sold':
      return 'sold';
    case 'expired':
      return 'expired';
    case 'terminated':
    case 'canceled':
    case 'cancelled':
      return 'terminated';
    default:
      return 'withdrawn';
  }
}

export interface ResolvedListingActions {
  primary: ActionId;
  secondary: ActionId[];
  menu: ActionId[];
  note?: { key: string; defaultValue: string };
  icons: boolean;
}

/** The actions a surface renders: the state's config minus signed-in-only and never-list actions. */
export function listingActionsFor(state: ListingState, { signedIn }: { signedIn: boolean }): ResolvedListingActions {
  const config = LISTING_ACTIONS[state];
  const allowed = (id: ActionId) => !config.never.includes(id) && (signedIn || !SIGNED_IN_ONLY.includes(id));
  return {
    primary: config.primary,
    secondary: config.secondary.filter(allowed),
    menu: config.menu.filter(allowed),
    note: config.note,
    icons: config.icons,
  };
}

/** Where an action can appear; cta_click.position uses these values. */
export type CtaPosition = 'hero' | 'rail' | 'top_bar' | 'phone_bar' | 'sheet_footer' | 'menu' | 'body' | 'lightbox' | 'photo_grid' | 'gallery_end';

/** Phone bar and top bar carry the primary; the top bar adds the first secondary. */
export const BAR_SLOTS = { phone: ['primary'], top: ['secondary', 'primary'] } as const;

/** The phone bar's height, safe area included; the page reserves the same at its foot. */
export const PHONE_BAR_RESERVE_PX = 106;

/** Below this width the phone layout and bottom bar apply; from it, the top bar. */
export const LISTING_TABLET_MIN_PX = 768;

/** From this width the title block and the 384 rail sit side by side. */
export const LISTING_RAIL_MIN_PX = 1024;

export const LISTING_RAIL_WIDTH_PX = 384;
